import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag, Tooltip } from 'antd';
import { CopyOutlined, FileExcelOutlined, PlusOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import CalculationDrawer from './CalculationDrawer';
import PaymentSlipModal from './PaymentSlipModal';
import { CALC_KINDS, STATUS_COLORS, errorText, money, saveBlob } from './format';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * Rent and other income calculated on the platform, with the return each one
 * was filed as and whether it is paid. A new month of the same flat starts
 * from the last one („copy for the next month").
 */
export default function CalculationsPanel({ company, projects, canManage, onOpenReturn }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({});
  const [drawer, setDrawer] = useState(null); // { id } | { template } | {}
  const [slip, setSlip] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tax-calculations', company, filters],
    queryFn: () => API.taxReturns.getCalculations({ company, ...filters }),
    enabled: Boolean(company),
  });
  const items = useMemo(() => data?.items || [], [data]);

  // Accounts the orders can be paid from: those seen on the company's statements.
  const { data: accounts = [] } = useQuery({
    queryKey: ['tax-returns-accounts', company],
    queryFn: () => API.taxReturns.getBankAccounts(company),
    enabled: Boolean(company && canManage),
  });
  const payerAccounts = accounts.map((a) => ({
    value: a.accountFormatted,
    label: `${a.accountFormatted}${a.project ? ` · ${a.project.code} ${a.project.name}` : ''}`,
  }));

  // The newest calculation of every flat, to start the next one from.
  const latestBySubject = useMemo(() => {
    const map = {};
    for (const c of items) {
      const prev = map[c.subject];
      if (!prev || c.period.year * 12 + c.period.month > prev.period.year * 12 + prev.period.month) map[c.subject] = c;
    }
    return map;
  }, [items]);

  const setFilter = (key, value) =>
    setFilters((f) => {
      const next = { ...f, [key]: value };
      if (value === undefined || value === null || value === '') delete next[key];
      return next;
    });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tax-calculations', company] });

  const exportMutation = useMutation({
    mutationFn: () =>
      API.taxReturns.exportCalculationOrders(company, filters, {
        sheet: t('calc.export.sheet'),
        project: t('fields.project'),
        subject: t('calc.fields.subject'),
        payer: t('calc.export.payer'),
        payerAccount: t('calc.fields.payerAccount'),
        purpose: t('calc.fields.purposeText'),
        paymentCode: t('slip.paymentCode'),
        amount: t('fields.amount'),
        payee: t('slip.payee'),
        payeeAccount: t('fields.paymentAccount'),
        model: t('slip.model'),
        reference: t('fields.bop'),
        returnNumber: t('calc.fields.returnNumber'),
      }),
    onSuccess: (blob) => saveBlob(blob, `${t('calc.export.fileName')}.xlsx`),
  });

  const statusLabel = (s) => t(`status.${s}`, { context: canManage ? undefined : 'client' });
  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1];
  }, []);

  const columns = [
    { title: t('fields.purpose'), dataIndex: 'pid', width: 115 },
    {
      title: t('fields.project'),
      dataIndex: 'project',
      width: 150,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
    {
      title: t('calc.fields.subject'),
      dataIndex: 'subject',
      render: (v, c) => (
        <div>
          <div>{v}</div>
          <div className="text-xs opacity-60">
            <Tag color={c.kind === 'rent' ? 'purple' : 'magenta'}>{t(`calc.kinds.${c.kind}`)}</Tag>
            {c.recipients.map((r) => r.name).join(', ')}
          </div>
        </div>
      ),
    },
    { title: t('calc.fields.months'), dataIndex: 'months', width: 70, align: 'right' },
    { title: t('calc.fields.netRsd'), dataIndex: ['totals', 'netRsd'], width: 140, align: 'right', render: money },
    {
      title: t('calc.fields.taxShort'),
      dataIndex: ['totals', 'tax'],
      width: 130,
      align: 'right',
      render: (v) => <b>{money(v)}</b>,
    },
    {
      title: t('fields.status'),
      width: 190,
      render: (c) => (
        <Space size={4} wrap>
          <Tag color={c.status === 'done' ? 'green' : 'default'}>{t(`calc.status.${c.status}`)}</Tag>
          {c.taxReturn && <Tag color={STATUS_COLORS[c.taxReturn.status]}>{statusLabel(c.taxReturn.status)}</Tag>}
        </Space>
      ),
    },
    canManage && {
      title: '',
      width: 60,
      align: 'center',
      render: (c) => (
        <Tooltip title={t('calc.copyNext')}>
          <Button
            shape="round"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setDrawer({ template: c });
            }}
          />
        </Tooltip>
      ),
    },
  ].filter(Boolean);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <p className="text-sm opacity-70">{t(canManage ? 'calc.help.panel' : 'calc.help.panelClient')}</p>
      <Space wrap>
        {canManage && (
          <Button shape="round" type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({})}>
            {t('calc.new')}
          </Button>
        )}
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder={t('filters.project')}
          value={filters.project}
          onChange={(v) => setFilter('project', v)}
          options={[
            ...projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` })),
            { value: 'none', label: t('filters.noProject') },
          ]}
        />
        <Select
          allowClear
          style={{ width: 110 }}
          placeholder={t('filters.year')}
          value={filters.year}
          onChange={(v) => setFilter('year', v)}
          options={years.map((y) => ({ value: y, label: y }))}
        />
        <Select
          allowClear
          style={{ width: 110 }}
          placeholder={t('filters.month')}
          value={filters.month}
          onChange={(v) => setFilter('month', v)}
          options={MONTHS.map((m) => ({ value: m, label: m }))}
        />
        <Select
          allowClear
          style={{ width: 170 }}
          placeholder={t('calc.fields.kind')}
          value={filters.kind}
          onChange={(v) => setFilter('kind', v)}
          options={CALC_KINDS.map((k) => ({ value: k, label: t(`calc.kinds.${k}`) }))}
        />
        <Tooltip title={t('calc.export.hint')}>
          <Button
            shape="round"
            icon={<FileExcelOutlined />}
            loading={exportMutation.isPending}
            disabled={!items.some((c) => c.status === 'done')}
            onClick={() => exportMutation.mutate()}
          >
            {t('calc.export.button')}
          </Button>
        </Tooltip>
      </Space>

      {(error || exportMutation.isError) && (
        <Alert type="error" showIcon message={errorText(t, error || exportMutation.error)} />
      )}

      {!isLoading && !items.length && !Object.keys(filters).length ? (
        <Empty style={{ margin: '32px 0' }} description={t(canManage ? 'calc.empty' : 'calc.emptyClient')} />
      ) : (
        <Table
          size="small"
          rowKey="_id"
          loading={isLoading}
          dataSource={items}
          columns={columns}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
          onRow={(c) => ({ onClick: () => setDrawer({ id: c._id }), style: { cursor: 'pointer' } })}
        />
      )}

      <CalculationDrawer
        open={Boolean(drawer)}
        company={company}
        id={drawer?.id || null}
        template={drawer?.template || null}
        projects={projects}
        payerAccounts={payerAccounts}
        subjects={data?.subjects || []}
        latestBySubject={latestBySubject}
        canManage={canManage}
        onClose={() => setDrawer(null)}
        onSaved={refresh}
        onSlip={setSlip}
        onOpenReturn={onOpenReturn}
      />
      <PaymentSlipModal
        slip={slip?.slip}
        title={slip?.title}
        note={t('calc.slipNote')}
        onClose={() => setSlip(null)}
      />
    </Space>
  );
}
